import { getRegistrationFormValues, isImcNumberDisabled } from './enrolmentDetailsLogic';
import { Box, Grid, Paper } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { practiceOfMedicineOptions } from '../../app/utils/util';
import AppTextInput from '../../app/components/AppTextInput';
import registrationFormModel from '../../app/models/pcsEnrolment/registrationFormModel';
import AppCheckboxField from '../../app/components/AppCheckboxField';
import { CheckBoxItem } from '../../app/models/common/checkBoxItem';
import { MapRegistrationTypesToAutocompleteItems, MapSchemesToAutocompleteItems, MapSpecialitiesToAutocompleteItems } from '../../app/utils/convertStaticDataToDropdownItems';
import { useAppSelector } from '../../app/store/configureStore';
import { SchemEnum } from '../../app/enums/schemEnum';
import AppAutocomplete from '../../app/components/AppAutocomplete';
import { AutocompleteItem } from '../../app/models/common/autocompleteItem';

interface Prop {
  reenrol: boolean;
}

export default function RegistrationDetails({ reenrol }: Prop) {

  const methods = useFormContext();
  const { control, reset, getValues, resetField } = methods;

  const { specialities, schemes, pcsStatuses, registrationTypes } = useAppSelector(state => state.ReferenceData);
  const { registrationDetails } = useAppSelector(state => state.pcs);

  const {
    registrationDetailsFormField: {
      IMCNumber,
      RegistrationType,
      Scheme,
      EngagedInPracticeOfMedicine,
      Specialty,
      SubSpecialty,
      UserConfirmation
    }
  } = registrationFormModel;

  const selectedScheme = useWatch({ control, name: Scheme.name });
  const filteredSpecialities: AutocompleteItem[] = MapSpecialitiesToAutocompleteItems(
    specialities?.filter(x => x.schemeId === (selectedScheme ?? registrationDetails?.schemeId)) ?? []
  );

  const [userConfirmationOptions,] = useState<CheckBoxItem[]>([{
    id: UserConfirmation.name,
    value: UserConfirmation.label
  }]);

  useEffect(() => {
    if (registrationDetails) {
      reset({ ...getValues(), ...getRegistrationFormValues(registrationDetails) }, {
        keepDirtyValues: true
      });
    }
  }, [registrationDetails, reset, getValues]);

  useEffect(() => {
    const practiceStatus = getValues(EngagedInPracticeOfMedicine.name);
    if (practiceStatus === undefined || practiceStatus === null || practiceStatus === '') {
      resetField(EngagedInPracticeOfMedicine.name, {
        defaultValue: registrationDetails?.engagedInPracticeOfMedicine === false ? '0' : '1'
      });
    }
  }, [registrationDetails, getValues, resetField, EngagedInPracticeOfMedicine.name]);

  return (
    <Box sx={{ minWidth: "100%" }} component={Paper}>
      <Grid container rowSpacing={4} sx={{ margin: "2%" }}>

        <Grid size={{ xs: 12 }}></Grid>

        <Grid size={{ xs: 12 }}>
          <AppTextInput control={control} name={IMCNumber.name} label={IMCNumber.label}
            disabled={isImcNumberDisabled(registrationDetails, pcsStatuses)}
            maxStringCount={IMCNumber.maxStringCount} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <AppAutocomplete control={control} name={RegistrationType.name} label={RegistrationType.label}
            information_text={reenrol === true ? RegistrationType.reenrol_information_text
              : RegistrationType.enrol_information_text}
            items={MapRegistrationTypesToAutocompleteItems(registrationTypes ?? [])} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <AppAutocomplete control={control} name={Scheme.name} label={Scheme.label}
            items={MapSchemesToAutocompleteItems(schemes ?? [])} information_text={Scheme.information_text} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <AppAutocomplete control={control} name={Specialty.name} label={Specialty.label}
            items={filteredSpecialities.length > 0 ? filteredSpecialities : []}
            information_text={Specialty.information_text} />
        </Grid>

        {schemes !== null && schemes.find(x => x.id === getValues(Scheme.name))?.code === SchemEnum.Surgery &&
          <>
            <Grid size={{ xs: 12 }}>
              <AppTextInput control={control} name={SubSpecialty.name} label={SubSpecialty.label}
                maxStringCount={SubSpecialty.maxStringCount} />
            </Grid>
          </>}

        <Grid size={{ xs: 12 }}>
          <AppAutocomplete control={control} items={practiceOfMedicineOptions()}
            html_information_text={EngagedInPracticeOfMedicine.information_text}
            name={EngagedInPracticeOfMedicine.name} label={EngagedInPracticeOfMedicine.label} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <AppCheckboxField control={control} name={UserConfirmation.name}
            label={UserConfirmation.label} options={userConfirmationOptions} fullRowClickable />
        </Grid>

        <Grid size={{ xs: 12 }}></Grid>

      </Grid>
    </Box>
  );
}
